import { useState } from "react";
import { motion } from "framer-motion";
import { FileDown, DraftingCompass, ExternalLink } from "lucide-react";
import { PRODUCTS, CATEGORIES, BRANDS, productAnchor } from "@/data/hvm";
import { track } from "@/utils/analytics";

export const ProductMatrix = () => {
  const [cat, setCat] = useState("all");
  const rows = PRODUCTS.filter((p) => cat === "all" || p.category === cat);

  return (
    <section id="solutions" data-testid="solutions-section" className="relative py-24 sm:py-32 noise-overlay">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="font-tech text-[11px] tracking-[0.3em] uppercase text-[#F4A031]"
        >
          The arsenal — cross-referenced
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="font-display font-bold uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight mt-4 max-w-3xl"
        >
          One threat.<br /><span className="text-[#F4A031]">70+ ways</span> to stop it.
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.18 }}
          className="text-[#94A3B8] mt-5 max-w-2xl"
        >
          Foundation depth, clear span, certified rating, duty cycle — the cross-reference
          exercise we run on every project, turned into a single view. A selection of the
          range below; the full catalogue runs to 70+ product lines.
        </motion.p>

        <div data-testid="brand-strip" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5 mt-12">
          {BRANDS.map((b, i) => (
            <motion.div
              key={b.name}
              data-testid={`brand-card-${b.name.split(" ")[0].toLowerCase()}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ delay: i * 0.07 }}
              className="bg-[#131927] border border-[#26334D] hover:border-[#F4A031]/60 clip-notch p-6 transition-colors"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="inline-flex items-center bg-[#0B0F17] border border-[#26334D] rounded-md px-3.5 h-11">
                  <img src={b.logo} alt={`${b.name} logo`} width="120" height="24" loading="lazy" className="max-h-6 max-w-[120px] object-contain" />
                </div>
                {b.origin && (
                  <span className="font-tech text-[10px] tracking-widest uppercase text-[#64748B]">{b.origin}</span>
                )}
              </div>
              <p className="font-grotesk text-sm text-[#F4A031] mt-2">{b.tagline}</p>
              <p className="text-sm text-[#94A3B8] leading-relaxed mt-2">{b.blurb}</p>
              <p className="font-tech text-[10px] tracking-[0.2em] uppercase text-[#64748B] mt-4 pt-3 border-t border-[#26334D]">
                {b.lines}
              </p>
            </motion.div>
          ))}
        </div>

        <div data-testid="matrix-filter-tabs" className="flex flex-wrap gap-2 mt-14">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              data-testid={`filter-tab-${c.id}`}
              onClick={() => setCat(c.id)}
              className={`font-tech text-[11px] tracking-[0.2em] uppercase px-4 py-2.5 clip-notch-sm border transition-colors ${
                cat === c.id
                  ? "bg-[#F4A031] border-[#F4A031] text-[#0B0F17] font-bold"
                  : "border-[#384B70] text-[#94A3B8] hover:border-[#F4A031] hover:text-white"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-6 border border-[#26334D] clip-notch overflow-hidden"
        >
          <div className="overflow-x-auto">
            <table data-testid="product-matrix-table" className="w-full min-w-[1060px] text-left">
              <thead>
                <tr className="bg-[#131927] font-tech text-[10px] tracking-[0.25em] uppercase text-[#64748B]">
                  <th className="px-5 py-4">Product</th>
                  <th className="px-5 py-4">Class / Rating</th>
                  <th className="px-5 py-4">Foundation</th>
                  <th className="px-5 py-4">Span / Spacing</th>
                  <th className="px-5 py-4">Best for</th>
                  <th className="px-5 py-4">Brochure</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p, i) => (
                  <tr
                    key={p.name}
                    id={productAnchor(p.name)}
                    data-testid={`matrix-row-${p.name.slice(0, 12).replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                    className="border-t border-[#26334D] hover:bg-[#131927] transition-colors group"
                  >
                    <td className="px-5 py-4">
                      <p className="font-grotesk font-semibold text-white text-sm group-hover:text-[#F4A031] transition-colors">{p.name}</p>
                      <p className="font-tech text-[10px] tracking-widest uppercase text-[#F4A031] mt-1">{p.brand}</p>
                    </td>
                    <td className="px-5 py-4 font-tech text-xs text-[#F8FAFC]">{p.rating}</td>
                    <td className="px-5 py-4 text-sm text-[#94A3B8]">{p.foundation}</td>
                    <td className="px-5 py-4 text-sm text-[#94A3B8]">{p.span}</td>
                    <td className="px-5 py-4 text-sm text-[#94A3B8]">{p.bestFor}</td>
                    <td className="px-5 py-4">
                      {p.brochure && (
                        <a
                          href={p.brochure}
                          onClick={() => track("brochure_download", p.name)}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`brochure-download-${i}`}
                          className="inline-flex items-center gap-2 font-tech text-[11px] tracking-widest uppercase text-[#F4A031] border border-[#F4A031]/40 hover:bg-[#F4A031] hover:text-[#0B0F17] px-3 py-2 clip-notch-sm transition-colors"
                        >
                          <FileDown size={13} /> PDF
                        </a>
                      )}
                      {p.drawing && (
                        <a
                          href={p.drawing}
                          onClick={() => track("drawing_download", p.name)}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`drawing-download-${i}`}
                          title="Shallow-mount (TFP) installation drawing"
                          className="inline-flex items-center gap-2 font-tech text-[11px] tracking-widest uppercase text-[#94A3B8] border border-[#26334D] hover:border-[#F4A031] hover:text-white px-3 py-2 clip-notch-sm transition-colors"
                        >
                          <DraftingCompass size={13} /> Drawing
                        </a>
                      )}
                      {!p.brochure && p.specUrl && (
                        <a
                          href={p.specUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`spec-link-${i}`}
                          className="inline-flex items-center gap-2 font-tech text-[11px] tracking-widest uppercase text-[#94A3B8] border border-[#384B70] hover:border-[#F4A031] hover:text-white px-3 py-2 clip-notch-sm transition-colors"
                        >
                          <ExternalLink size={13} /> Specs
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <p className="font-tech text-[11px] tracking-widest uppercase text-[#64748B] mt-5">
          Not sure which row is yours? <a href="#assessment" data-testid="matrix-cta-link" className="text-[#F4A031] hover:underline">Send us the site details →</a>
        </p>
        <p className="font-tech text-[11px] tracking-widest uppercase text-[#64748B] mt-2">
          Protecting an event or temporary crowded place? <a href="/blog/temporary-hvm-for-events" data-testid="matrix-events-guide-link" className="text-[#F4A031] hover:underline">Read the temporary HVM guide →</a>
        </p>
        <p className="font-tech text-[10px] tracking-widest uppercase text-[#64748B] mt-2">
          All PDFs are official manufacturer datasheets &amp; certificates — free to download, no email required.
        </p>
      </div>
    </section>
  );
};
